import { Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { AlertController, LoadingController, ModalController, ToastController, } from '@ionic/angular';
import { ActivatedRoute, Router } from '@angular/router';
import { GoalsService } from '../goals.service';
import { BaseClass } from 'src/app/shared/classes/base';
import { Goal, GoalStatusEnum } from '../_interfaces/goal.interface';
import { SubordinateGoalStatus } from '../_interfaces/subordinate-goals-status.enum';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { ProcessConfig, QuestionConfig, QuestionGroup, } from 'src/app/shared/interfaces/process-config.interface';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TwoPlusTwoCoaching, TwoPlusTwoCoachingStatus, } from '../_interfaces/two-plus-two-coaching.interface';
import { ProcessStatusEnum } from 'src/app/shared/interfaces/process.interface';
import { AutoSaveComponent } from 'src/app/shared/components/autosave/autosave.component';
import { AttributeItem } from 'src/app/shared/interfaces/attribute-item';
import { environment } from 'src/environments/environment';
import { UtilService } from '../../shared/services/util.service';
import { NewGoalsInput } from 'src/app/shared/modals/new-goals-input/new-goals-input';
import { QuestionAttributesPipe } from 'src/app/shared/pipes/question-attributes.pipe';
import { QuestionAttributePipe } from 'src/app/shared/pipes/question-attribute.pipe';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';

@Component({
    selector: 'app-goals-review',
    templateUrl: './goals-review.page.html',
    styleUrls: ['./goals-review.page.scss'],
})
export class GoalsReviewPage extends BaseClass implements OnDestroy {
    @ViewChild('autosave', { static: true }) autosave: AutoSaveComponent;

    public highlightSelfOrManagerEvalAnswer: boolean = environment.features.highlightSelfOrManagerEvalAnswer;
    public hideGoalsOnGoalsReview: boolean = environment.features.hideGoalsOnGoalsReview;
    public questionsType;

    public coachingForm: UntypedFormGroup;
    public user: { attributes: AttributeItem[] };
    public inputConfig: ProcessConfig;
    public reviewConfig: ProcessConfig;
    public coachingConfig: ProcessConfig;
    public _processStatus: ProcessStatusEnum;
    public goals: Array<Goal>;
    public displayedGoals: Array<Goal> = [];
    public SubordinateGoalStatus = SubordinateGoalStatus;
    public canSaveReview: boolean = false;
    public allowAutoSave: boolean = true;
    public autoSaving: boolean = false;
    public hasPendingChanges: boolean = false;
    public canSubmitGoals: boolean = false;
    public canEdit: boolean = false;
    public isUserAttributesInsideConsentGoals: boolean = false;

    private _searchSubject: Subject<string> = new Subject();
    private _participantId: number;
    public selectedLanguage: string;
    private _returnPath: string = undefined;

    private readonly _cycleId: number;
    private readonly _processId: number;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            public alertController: AlertController,
            private _modalController: ModalController,
            private _loadingController: LoadingController,
            private _router: Router,
            private _goalsService: GoalsService,
            private _activatedRoute: ActivatedRoute,
            private _questionAttributePipe: QuestionAttributePipe,
            private _utilService: UtilService,
            private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this.questionsType = this._configService.settings.features.questionTypeAvailability.question

        this._processId = this._router.getCurrentNavigation().extras.state
                ? parseInt(this._router.getCurrentNavigation().extras.state.processId)
                :null;
        this._utilService.insertProcessIdInLocalStorage(this._processId);

        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'),
                10
        );
        this._setSearchSubscription();
    }

    ionViewWillEnter() {
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        this._loadGoals(this._cycleId);
        this._returnPath = localStorage.getItem('GoalsReview_ReturnPath');
    }

    public goBack(): void {
        this._utilService.removeProcessIdFromLocalStorage();
        this._router.navigate([this._returnPath ? this._returnPath:'dashboard']);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
    }

    public setDate(event, index) {
        const field = 'field' + (index + 1);
        const ind = event.indexOf('T');
        if (ind === -1) {
            return;
        }
        const date: string = event.slice(0, ind);
        this.coachingForm.get(field).setValue(date);
    }

    public hasGoals(goals: Array<Goal>): boolean {
        return goals && goals.length > 0;
    }

    public canAddGoal(goals: Array<Goal>): boolean {
        return this.canEdit;
    }

    public canEditGoals(status: GoalStatusEnum): boolean {
        const validStatuses = [
            GoalStatusEnum.Draft,
            GoalStatusEnum.WaitingForApproval,
            GoalStatusEnum.Approved,
            GoalStatusEnum.DraftForReview,
            GoalStatusEnum.ReviewRejected,
            GoalStatusEnum.SentForReview
        ];
        return (this.canEdit && validStatuses.includes(status) || this._processStatus !== ProcessStatusEnum.AllowedViewing);
    }

    public getColumnSize(questionsCount: number): number {
        let tableSize = this.isUserAttributesInsideConsentGoals ? 12:10;
        if (this.inputConfig) {
            tableSize = this.inputConfig.title ? tableSize - 2:tableSize;
            tableSize = this.inputConfig.weight ? tableSize - 2:tableSize;
            tableSize = this.inputConfig.deadline ? tableSize - 2:tableSize;
            tableSize = this.inputConfig.description ? tableSize - 2:tableSize;
        }

        let columnSize = questionsCount ? parseFloat((tableSize / questionsCount).toFixed(2).toString()):null;

        if (columnSize)
            return columnSize;
        return questionsCount > 1 ? 1:2;
    }

    public async confirmDeleteGoal(goal: Goal): Promise<void> {
        const alert = await this.alertController.create({
            message: this._translationService.getTranslation(
                    'goalProgressInfo.confirmDeleteGoal'
            ),
            buttons: [
                {
                    text: this._translationService.getTranslation('generic.no'),
                    role: 'cancel',
                    cssClass: 'danger',
                },
                {
                    text: this._translationService.getTranslation('generic.yes'),
                    handler: () => {
                        this._deleteGoal(goal.id);
                    },
                },
            ],
        });

        await alert.present();
    }

    private _deleteGoal(goalId: number): void {
        this._goalsService.deleteGoal(goalId, true).subscribe(
                () => {
                    this._loadGoals(this._cycleId);
                    this.notify(
                            this._translationService.getTranslation(
                                    'goalsInput.goalDeleteSuccess'
                            ),
                            'success'
                    );
                },
                (error) => this.notify(this.getErrorNotification(error), 'danger')
        );
    }

    public async openManageGoalsInputModal(goal: Goal = null) {
        if (!goal && this.goals.length >= this.inputConfig.maximumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.maxGoalsQuantityReached'
                    ),
                    'danger'
            );
            return;
        }

        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: 'new-goals-input',
            componentProps: {
                cycleParticipantId: this._participantId,
                processConfig: this.inputConfig,
                goal: goal,
                isGoalReview: true,
                processType: this.ProcessType.GoalsReview,
            },
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                this._loadGoals(this._cycleId);
            }
        });

        return await modal.present();
    }

    public updateSearch(searchTextValue: string) {
        this._searchSubject.next(searchTextValue);
    }

    saveClicked() {
        this.saveCoachingRecord(false, false);
    }


    public saveCoachingRecord(preventNotification: boolean = false, isAutoSaving?: boolean): void {
        this.autoSaving = true;
        let coaching: TwoPlusTwoCoaching = this.coachingForm ? this.coachingForm.getRawValue():{};
        coaching.cycleParticipantId = this._participantId;
        this._goalsService.manageCoachingRecord(coaching).subscribe(
                () => {
                    if (!preventNotification) {
                        this.hasPendingChanges = false;
                        this.notify(
                                this._translationService.getTranslation(
                                        'goalsReview.saveCoachingSuccess'
                                ),
                                'success'
                        );
                    }

                    this.autoSaving = false;
                },
                (error) => {
                    this.notify(this.getErrorNotification(error), 'danger');
                    this.autoSaving = false;
                }
        );
    }

    public submitGoals(goals: Array<Goal>): void {
        if (this.coachingForm.valid) {
            const coaching: TwoPlusTwoCoaching = this.coachingForm ? this.coachingForm.getRawValue():{};
            const isTwoPlusTwoCoachingIntegration = this.coachingConfig.twoPlusTwoCoachingIntegration;
            const goalsIds = goals.map((goal) => goal.id);
            if (this.hideGoalsOnGoalsReview || (goalsIds.length > 0 && this._validateGoals(goals))) {
                this._submitGoalsReviewAndCoaching(goalsIds, coaching, isTwoPlusTwoCoachingIntegration);
            }
        } else {
            this.notify(
                    this._translationService.getTranslation('generic.missingFields'),
                    'danger'
            );
        }
    }

    public getQuestionIndex(question: QuestionConfig) {
        const index = this.coachingConfig.questions.findIndex(q => q.id === question.id);
        return index === -1 ? index:index + 1;
    }

    public getVisibleQuestionIndex(question: QuestionConfig) {
        const index = this.coachingConfig.questions.filter(q => !q.hideQuestion).findIndex(q => q.id === question.id);
        return index === -1 ? index:index + 1;
    }

    private _submitGoalsReviewAndCoaching(
            goalsIds: Array<number>,
            coaching: TwoPlusTwoCoaching,
            istwoPlusTwoCoachingIntegration: boolean
    ): void {
        this._goalsService.submitGoalsReview(goalsIds, coaching, this._cycleId, istwoPlusTwoCoachingIntegration).subscribe(
                () => {
                    this.notify(
                            this._translationService.getTranslation(
                                    'goalsReview.sendReviewSuccess'
                            ),
                            'success'
                    );
                    this.coachingForm = null;
                    this.goBack();
                },
                (error) => this.notify(this.getErrorNotification(error), 'danger')
        );
    }

    private _setSearchSubscription() {
        this._searchSubject
                .pipe(
                        distinctUntilChanged(),
                        debounceTime(1000)
                )
                .subscribe((searchValue: string) => {
                    this.displayedGoals = this.goals.filter((goal) => {
                        return goal.title
                                .toLowerCase()
                                .includes(searchValue.toLowerCase().trim());
                    });
                });
    }

    private async _loadGoals(cycleId: number): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.getUserGoalsForReview(cycleId).subscribe(
                (response) => {
                    this._participantId = response.data.cycleParticipantId;

                    this.user = response.data.user;
                    this.inputConfig = response.data.goalInputConfig;
                    this.reviewConfig = response.data.goalReviewConfig;

                    this.goals = response.data.goals;
                    this.displayedGoals = response.data.goals;
                    this._processStatus = response.data.processStatus;
                    this.isUserAttributesInsideConsentGoals = response.data.isUserAttributesInsideConsentGoals;

                    const validStatuses = [
                        GoalStatusEnum.Draft,
                        GoalStatusEnum.WaitingForApproval,
                        GoalStatusEnum.Rejected,
                        GoalStatusEnum.Approved,
                        GoalStatusEnum.DraftForReview,
                        GoalStatusEnum.ReviewRejected,
                        GoalStatusEnum.ReviewAccepted,
                        GoalStatusEnum.SentForReview
                    ];

                    this.canEdit =
                            this._processStatus === ProcessStatusEnum.WaitingInput ||
                            this._processStatus === ProcessStatusEnum.Declined;

                    this.canSubmitGoals =
                            ((this._processStatus === ProcessStatusEnum.WaitingInput || this._processStatus === ProcessStatusEnum.Declined) &&
                                    ((this.goals.some((goal) => validStatuses.includes(goal.status) &&
                                                    (goal.status === GoalStatusEnum.WaitingForApproval ||
                                                            goal.status === GoalStatusEnum.Rejected ||
                                                            goal.status === GoalStatusEnum.DraftForReview ||
                                                            goal.status === GoalStatusEnum.SentForReview ||
                                                            goal.status === GoalStatusEnum.ReviewRejected || this.canEdit))) ||
                                            !this.reviewConfig.twoPlusTwoCoachingIntegration ||
                                            this.hideGoalsOnGoalsReview || this.isUserAttributesInsideConsentGoals));

                    if (!this.coachingForm) {
                        this.coachingConfig = response.data.twoPlusTwoCoachingConfig;
                        if (this.coachingConfig !== null && this.coachingConfig.questionsGroups && this.coachingConfig.questionsGroups.length > 0) {
                            this.coachingForm = this._createCoachingGroupFormGroup(
                                    this.coachingConfig.questionsGroups,
                                    response.data.userTwoPlusTwoCoachingRecord
                            );
                        } else if (this.coachingConfig !== null && this.coachingConfig.questionsGroups && this.coachingConfig.questions.length > 0) {
                            this.coachingForm = this._createCoachingQuestionsFormGroup(
                                    this.coachingConfig.questions,
                                    response.data.userTwoPlusTwoCoachingRecord
                            );
                        }

                        if (this.autosave) {
                            this.autosave.autoSaveAllowed$.subscribe((value: boolean) => {
                                this.allowAutoSave = value;
                            });
                        }
                        if (this.coachingForm) {
                            this.coachingForm.valueChanges
                                    .pipe(
                                            distinctUntilChanged(),
                                            debounceTime(15000)
                                    )
                                    .subscribe(() => {
                                        if (this.allowAutoSave) {
                                            this.hasPendingChanges = this.autoSaving && true;

                                            if (!this.autoSaving) {
                                                this.saveCoachingRecord(true);
                                            }
                                        }
                                    });
                        }
                    }
                    loading.dismiss();
                },
                (error) => {
                    loading.dismiss();
                    this.notify(this.getErrorNotification(error), 'danger');
                }
        );
    }

    private _createCoachingQuestionsFormGroup(
            questions: Array<QuestionConfig>,
            coachingRecord: TwoPlusTwoCoaching = null
    ): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            managerComment: new UntypedFormControl(
                    coachingRecord ? coachingRecord.managerComment:''
            ),
        });

        questions = this._questionAttributePipe.transform(
                questions,
                this.user.attributes
        );

        questions.forEach((question, index) => {
            const validators = !question.hideQuestion
                    ? [Validators.required]
                    :[];

            const controlName = 'field' + (index + 1);
            formGroup.addControl(
                    controlName,
                    new UntypedFormControl(
                            {
                                value: coachingRecord ? coachingRecord[controlName]:'',
                                disabled:
                                        coachingRecord &&
                                        coachingRecord.status !== TwoPlusTwoCoachingStatus.Draft &&
                                        question.typeConfig.type !== this.QuestionType.Text,
                            },
                            validators
                    )
            );

            const commentControl = 'field' + (index + 1) + 'Comment';
            formGroup.addControl(
                    commentControl,
                    new UntypedFormControl(coachingRecord ? coachingRecord[commentControl]:'')
            );
        });

        this.canSaveReview = this.canEdit;

        return formGroup;
    }

    private _createCoachingGroupFormGroup(
            groups: Array<QuestionGroup>,
            coachingRecord: TwoPlusTwoCoaching = null
    ): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            managerComment: new UntypedFormControl(
                    coachingRecord ? coachingRecord.managerComment:''
            ),
        });

        let currentIndex = 1;
        groups.forEach((group) => {
            group.questions.forEach((question) => {
                const controlName = 'field' + currentIndex;
                formGroup.addControl(
                        controlName,
                        new UntypedFormControl({
                            value: coachingRecord ? coachingRecord[controlName]:'',
                            disabled:
                                    coachingRecord &&
                                    coachingRecord.status !== TwoPlusTwoCoachingStatus.Draft,
                        })
                );

                const commentControl = 'field' + (currentIndex + 1) + 'Comment';
                formGroup.addControl(
                        commentControl,
                        new UntypedFormControl(coachingRecord ? coachingRecord[commentControl]:'')
                );

                question.currentIndex = currentIndex;
                currentIndex++;
            });
        });

        this.canSaveReview = this.canEdit;

        return formGroup;
    }

    public showRequiredBadge(formGroup: UntypedFormGroup, index: number): boolean {
        return formGroup['missingFields'] && !formGroup.get('field' + index).valid;
    }

    getValidatedField(goal: GoalStatusEnum) {
        return goal === this.GoalStatus.ReviewAccepted && this._processStatus === ProcessStatusEnum.AllowedViewing;
    }

    public setRangeValue(key: string, value: string): void {
        if (!this.coachingForm.get(key).disabled)
            this.coachingForm.get(key).setValue(value);
    }


    private _validateGoals(goals: Array<Goal>): boolean {
        if (this.inputConfig && goals.length < this.inputConfig.minimumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.MinimumGoalsNotReached'
                    ), 'danger'
            );
            return false;
        }
        if (this.inputConfig && goals.length > this.inputConfig.maximumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.maxGoalsQuantityReached'
                    ), 'danger'
            );
            return false;
        }
        const weightSum = goals.reduce((sum, goal) => sum + goal.weight, 0);
        if (weightSum !== 100) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.weightsWrongSum'
                    ), 'danger'
            );
            return false;
        }
        return true;
    }


    @HostListener('window:beforeunload')
    ngOnDestroy() {
        this._searchSubject.unsubscribe();
        localStorage.removeItem('GoalsReview_ReturnPath');
    }

    public onSelectChange(event: any, index: number) {
        const selectedValues = event.detail.value.join('; ');
        this.coachingForm.controls['field' + (index + 1)].setValue(selectedValues);
    }
}
